import type { UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query';
import { upperFirst } from 'lodash-es';

type TPreventUndefined = 'PreventUndefined' | false;

export type UseResourceInfiniteQueryResult<
  TData,
  TError,
  ResourceName extends string,
  PreventUndefined extends TPreventUndefined = false,
> = {
  [_K in 'data' as `${ResourceName}`]: PreventUndefined extends 'PreventUndefined'
    ? { pages: TData[]; pageParams: unknown[] }
    : { pages: TData[]; pageParams: unknown[] } | undefined;
} & {
  [K in 'error' as `${ResourceName}${Capitalize<K>}`]: TError | null;
} & {
  [_K in 'isFetching' as `is${Capitalize<ResourceName>}Fetching`]: boolean;
} & {
  [_K in 'isFetchingNextPage' as `is${Capitalize<ResourceName>}FetchingNextPage`]: boolean;
} & {
  [_K in 'isFetchingPreviousPage' as `is${Capitalize<ResourceName>}FetchingPreviousPage`]: boolean;
} & {
  [_K in 'isRefetching' as `is${Capitalize<ResourceName>}Refetching`]: boolean;
} & {
  [_K in 'isLoading' as `is${Capitalize<ResourceName>}Loading`]: boolean;
} & {
  [_K in 'isInitialLoading' as `is${Capitalize<ResourceName>}InitialLoading`]: boolean;
} & {
  [_K in 'hasNextPage' as `has${Capitalize<ResourceName>}NextPage`]: boolean | undefined;
} & {
  [_K in 'hasPreviousPage' as `has${Capitalize<ResourceName>}PreviousPage`]: boolean | undefined;
} & {
  [_K in 'fetchNextPage' as `fetchNext${Capitalize<ResourceName>}`]: UseInfiniteQueryResult<
    TData,
    TError
  >['fetchNextPage'];
} & {
  [_K in 'fetchPreviousPage' as `fetchPrevious${Capitalize<ResourceName>}`]: UseInfiniteQueryResult<
    TData,
    TError
  >['fetchPreviousPage'];
} & {
  [K in 'refetch' as `${K}${Capitalize<ResourceName>}`]: UseInfiniteQueryResult<
    TData,
    TError
  >['refetch'];
};

export function addResourceNameToInfiniteQueryResult<
  TData,
  TError,
  ResourceName extends string,
  PreventUndefined extends TPreventUndefined = false,
>(
  resourceName: ResourceName,
  queryResult: ReturnType<typeof useInfiniteQuery<TData, TError>>,
  ...fallback: PreventUndefined extends 'PreventUndefined'
    ? [fallback: { pages: TData[]; pageParams: unknown[] }]
    : []
): UseResourceInfiniteQueryResult<TData, TError, ResourceName, PreventUndefined> {
  const resourceNameCapitalised = upperFirst(resourceName);

  const {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isRefetching,
    isLoading,
    isPlaceholderData,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
  } = queryResult;

  return {
    [resourceName]: data ?? fallback[0],
    [`${resourceName}Error`]: error,
    [`is${resourceNameCapitalised}Fetching`]: isFetching,
    [`is${resourceNameCapitalised}FetchingNextPage`]: isFetchingNextPage,
    [`is${resourceNameCapitalised}FetchingPreviousPage`]: isFetchingPreviousPage,
    [`is${resourceNameCapitalised}Refetching`]: isRefetching,
    [`is${resourceNameCapitalised}Loading`]: isLoading,
    [`is${resourceNameCapitalised}InitialLoading`]: isFetching && isPlaceholderData,
    [`has${resourceNameCapitalised}NextPage`]: hasNextPage,
    [`has${resourceNameCapitalised}PreviousPage`]: hasPreviousPage,
    [`fetchNext${resourceNameCapitalised}`]: fetchNextPage,
    [`fetchPrevious${resourceNameCapitalised}`]: fetchPreviousPage,
    [`refetch${resourceNameCapitalised}`]: refetch,
  } as UseResourceInfiniteQueryResult<TData, TError, ResourceName, PreventUndefined>;
}
